const moment = require('moment');

// This regular expression is also present in the agent to capture header
// timings, so if you change it here, you'll also need to change it in the
// agent.
//
// So we can delete ANSI codes from header names
const ANSI_REGEXP = /\x1b\[(?:[;\d]+)?[mK]/g; // eslint-disable-line no-control-regex
// Parse the group name from the group
const GROUP_REGEX = /^(?:---|\+\+\+|~~~)\s(.*)$/;
// This lets us find the default default group to expand when first displaying the log
const RUNNING_REGEX = /Running (?:(?:build |batch )?script|commands?|(?:local|global) command hook)/;

export function parse(log, output, headerTimes) {
  let header; // eslint-disable-line no-unused-vars
  let index;
  let lineNumber;
  const lines = output.split('\n');

  // Remove the last line if it's blank
  const lastIndex = lines.length - 1;
  if (lines[lastIndex] === "") {
    lines.splice(lastIndex, 1);
  }

  // The groups as they're collected
  const groups = [];

  // The current group used within the loop
  let group = null;

  // Whether or not the log contains text that looks like a SSH key failure
  let sshKeyFailure = false;

  // Whether or not the log contains text that looksl ike the commit wasn't found in the branch
  let gitCommitNotFoundInBranchFailure = false;

  // Shortcut function to create a group
  const createGroup = function(name, number) {
    group = {
      id: `${log.id}/${number}`,
      number,
      name,
      lines: [],
      expanded: false
    };

    // Add the group to the groups array
    groups.push(group);

    return group;
  };

  // Has a header already been expanded by a +
  let alreadyExpanded = false;

  // If the first line in the output isn't a group, we'll need to create a null group.
  const firstLine = lines[0];
  const strippedFirstLine = firstLine.replace(ANSI_REGEXP, "");
  if (!(header = GROUP_REGEX.test(strippedFirstLine))) {
    group = createGroup();
  }

  // Parse the output
  for (index = 0; index < lines.length; index++) {
    let groupMatch;
    const line = lines[index];
    lineNumber = index + 1;

    // Strip ANSI codes from the line
    const strippedLine = line.replace(ANSI_REGEXP, "");

    const prefix = strippedLine[0];

    // We first start with a quick check, so we don't regex every line.
    if (((prefix === "-") || (prefix === "+") || (prefix === "~")) && (groupMatch = GROUP_REGEX.exec(line))) {
      // Grab the group name once we know it's a group
      const groupName = groupMatch[1];

      // Create the new group
      group = createGroup(groupName, lineNumber);

      // If they've use the + syntax, it's open by default.
      if (prefix === "+") {
        group.expanded = true;
        alreadyExpanded = true;
      // If it's using the ~ syntax, then it's from the bootstrap file
      } else if (prefix === "~") {
        group.bootstrap = true;
      }

    } else if (strippedLine === "^^^ +++") {
      // If they've used the `^^^ +++` magic string, then force the current
      // group to open.
      group.expanded = true;
    } else {
      // If the line contains text that may insinuate that the build failed
      // because of an SSH key failure
      if (!sshKeyFailure && (line === "Permission denied (publickey).")) {
        sshKeyFailure = true;
      }

      // If the line contains the git error message from doing a `git checkout -f`
      // then perhaps the commit no longer exists on the branch. So let's
      // detect it so we can show a nice message to help them.
      // See https://github.com/buildkite/agent/issues/581
      if (!gitCommitNotFoundInBranchFailure && (line.indexOf("fatal: reference is not a tree:") === 0)) {
        gitCommitNotFoundInBranchFailure = true;
      }

      group.lines.push({ id: `${group.id}-${lineNumber}`, number: lineNumber, line });
    }
  }

  // As we calculate the durations, track the longest duration, so we can do
  // relative time calculations
  let longestDuration = 0;

  // Grab the header times from the JSON
  if (!headerTimes) { headerTimes = []; }

  if (headerTimes && headerTimes.length > 0) {
    // The header times are in order that they appear in the log. So the
    // finishedAt is the previous headers startedAt
    const previousStartedAt = null; // eslint-disable-line no-unused-vars

    for (index = 0; index < groups.length; index++) {
      group = groups[index];
      group.startedAt = headerTimes[index];
      group.finishedAt = headerTimes[index + 1];

      // Calculate the duration of the group, and track the largest one
      if (group.startedAt && group.finishedAt) {
        group.duration = moment(group.finishedAt).diff(group.startedAt);

        if (group.duration > longestDuration) {
          longestDuration = group.duration;
        }
      }
    }
  }

  // Calculate the duration percentage for the groups
  for (group of groups) {
    if (group.duration != null) {
      group.durationPercentage = Math.floor((group.duration / longestDuration) * 100.0);
    }
  }

  // If we're streaming, don't do the smart expanding
  if (!log.streaming) {
    // If nothing has been expanded, expand the last non-bootstrap header
    if (!alreadyExpanded) {
      for (group of groups.slice(0).reverse()) {
        if (!group.bootstrap) {
          group.expanded = true;
          alreadyExpanded = true;
          break;
        }
      }
    }

    // If _still_ nothing is expanded, lets auto-expand the running build script one
    if (!alreadyExpanded) {
      for (group of groups) {
        if (RUNNING_REGEX.test(group.name)) {
          group.expanded = true;
          alreadyExpanded = true;
          break;
        }
      }
    }

    // After all this hard work, nothing is expanded (which could be the case
    // if a script doesn't get passed a git clone stage) then show the last
    // group.
    if (!alreadyExpanded) {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup) {
        lastGroup.expanded = true;
      }
    }
  }

  // Return the state and final result of this parse.
  return { groups, totalLines: lineNumber, sshKeyFailure, gitCommitNotFoundInBranchFailure };
}
